import type { Schema } from "@/shared/components/Form/types";
import type {
  CreateSchemaDefinition,
  EditSchemaDefinition,
} from "@/shared/components/CRUD/types";
import {
  CreateUserMutation,
  UpdateUserMutation,
  UserEditQuery,
  UserShowQuery,
  type UpdateUserInput,
} from "@/admin/entities/users";
import type {
  UserEditQueryType,
  UserShowQueryType,
} from "@/types/graphql/admin.generated";
import { PlatformAutocomplete } from "@/admin/entities/platforms";
import { UserOrganizationAutocomplete } from "@/admin/entities/organizationUsers";
import { OrganizationsAutocompleteQuery } from "@/admin/entities/organizations";
import { getTimezoneOptions } from "@/shared/utils/timezones";
import { countryOptions } from "@/shared/utils/countries";
import { userLanguageOptions } from "@/shared/utils/userLanguages";

/**
 * Схемы форм для feature управления пользователями
 * Используют entity мутации и запросы
 */

// Фильтры списка пользователей
export const filterSchema: Schema = {
  search: {
    label: "Поиск",
    type: "text",
    placeholder: "Поиск по email, имени...",
    xs: 12,
    fullWidth: true,
    priority: "primary",
  },
};

// Схема создания пользователя
export const createSchema: CreateSchemaDefinition = {
  main: {
    label: "Создание пользователя",
    schema: {
      email: {
        label: "Email",
        type: "email",
        xs: 12,
        md: 6,
        fullWidth: true,
      },
      phone: {
        label: "Номер телефона",
        type: "text",
        xs: 12,
        md: 6,
        fullWidth: true,
      },
      lastName: {
        label: "Фамилия",
        type: "text",
        required: true,
        xs: 12,
        md: 4,
        fullWidth: true,
      },
      firstName: {
        label: "Имя",
        type: "text",
        required: true,
        xs: 12,
        md: 4,
        fullWidth: true,
      },
      middleName: {
        label: "Отчество",
        type: "text",
        xs: 12,
        md: 4,
        fullWidth: true,
      },
      avatarId: {
        label: "Аватар",
        type: "image",
        imageType: "avatar",
        xs: 12,
        fullWidth: true,
      },
      isAdmin: {
        label: "Администратор",
        type: "checkbox",
        helperText: "Предоставляет доступ к административному интерфейсу",
        xs: 12,
        fullWidth: true,
      },
      isPartner: {
        label: "Партнёр",
        type: "checkbox",
        helperText:
          "Сразу создать пользователя партнёром (доступ в партнёрский кабинет)",
        xs: 12,
        fullWidth: true,
      },
      currentOrganizationId: {
        label: "Текущая организация",
        type: "autocomplete",
        query: OrganizationsAutocompleteQuery,
        helperText:
          "Сразу привязать партнёра к организации (членство + текущая) — доступно только вместе с «Партнёр»",
        disabledCheck: ({ values }: { values: Record<string, any> }) =>
          !values.isPartner,
        clearWhenDisabled: true,
        xs: 12,
        fullWidth: true,
      },
    },
    createMutation: CreateUserMutation,
  },
} as const;

// Заголовок диалога — общий для всех шагов
function getUserTitle(data: unknown): string | null {
  const typed = data as UserShowQueryType;
  const user = typed?.user;
  if (!user) return null;

  const fullName = [user.firstName, user.middleName, user.lastName]
    .filter(Boolean)
    .join(" ");

  return fullName || user.displayName || user.email || user.phone || null;
}

// Схема редактирования пользователя.
//
// Структура — плоские КОРНЕВЫЕ шаги диалога (референс:
// partner/src/admin/routes/Users.js:192-472 — User Details / Main settings /
// Optional settings / Partner settings / Admin), без вложенных табов внутри
// формы. У каждого шага СВОИ editQuery/getEditData/updateMutation с узким
// набором полей — сабмит вкладки «Партнёр» шлёт только партнёрские поля и
// не перетирает основные (реф-механика: каждый шаг Users.js несёт свой
// getEditData). Тот же плоский паттерн уже используют organizations
// (admin/features/organizations/model/schemas.tsx).
export const editSchemaBase: EditSchemaDefinition = {
  details: {
    label: "Просмотр",
    type: "show" as const,
    getTitle: getUserTitle,
    showQuery: UserShowQuery,
  },
  main: {
    label: "Основное",
    type: "form" as const,
    getTitle: getUserTitle,
    updateMutation: UpdateUserMutation,
    editQuery: UserEditQuery,
    schema: {
      email: {
        label: "Email",
        type: "email",
        xs: 6,
        fullWidth: true,
      },
      phone: {
        label: "Номер телефона",
        type: "text",
        xs: 6,
        fullWidth: true,
      },
      lastName: {
        label: "Фамилия",
        type: "text",
        xs: 12,
        md: 4,
        fullWidth: true,
      },
      firstName: {
        label: "Имя",
        type: "text",
        xs: 12,
        md: 4,
        fullWidth: true,
      },
      middleName: {
        label: "Отчество",
        type: "text",
        xs: 12,
        md: 4,
        fullWidth: true,
      },
      avatarId: {
        label: "Аватар",
        type: "image",
        imageType: "avatar",
        width: 200,
        height: 200,
        xs: 12,
        md: 4,
      },
    },
    getEditData: (data: unknown): UpdateUserInput => {
      const user = (data as UserEditQueryType)?.user;
      return {
        email: user?.email,
        phone: user?.phone,
        firstName: user?.firstName,
        lastName: user?.lastName,
        middleName: user?.middleName,
        avatarId: user?.avatar?.id,
      };
    },
  },
  profile: {
    label: "Профиль",
    type: "form" as const,
    getTitle: getUserTitle,
    updateMutation: UpdateUserMutation,
    editQuery: UserEditQuery,
    schema: {
      country: {
        label: "Страна",
        type: "autocomplete",
        options: countryOptions,
        xs: 12,
        md: 6,
        fullWidth: true,
      },
      timeZone: {
        label: "Часовой пояс",
        type: "autocomplete",
        xs: 12,
        md: 6,
        options: getTimezoneOptions,
        fullWidth: true,
      },
      language: {
        label: "Язык",
        type: "autocomplete",
        options: userLanguageOptions,
        xs: 12,
        md: 6,
        fullWidth: true,
      },
      agreeToMarketingNewsletters: {
        label: "Согласие на маркетинговую рассылку",
        type: "checkbox",
        xs: 12,
        fullWidth: true,
      },
    },
    getEditData: (data: unknown): UpdateUserInput => {
      const user = (data as UserEditQueryType)?.user;
      return {
        country: user?.country,
        timeZone: user?.timeZone,
        language: user?.language,
        agreeToMarketingNewsletters: user?.agreeToMarketingNewsletters || false,
      };
    },
  },
  partner: {
    label: "Партнёр",
    type: "form" as const,
    getTitle: getUserTitle,
    updateMutation: UpdateUserMutation,
    editQuery: UserEditQuery,
    schema: {
      isPartner: {
        label: "Партнёр",
        type: "checkbox",
        helperText:
          "Нельзя включить, если у пользователя задана платформа (platformId) — эти два поля взаимоисключающие.",
        disabledCheck: ({ values }: { values: Record<string, any> }) =>
          Boolean(values.platformId),
        xs: 12,
        fullWidth: true,
      },
      platformId: {
        label: "Платформа",
        type: "fieldComponent",
        xs: 12,
        fullWidth: true,
        disabledCheck: ({ values }: { values: Record<string, any> }) =>
          Boolean(values.isPartner),
        fieldComponent: ({ onChange, value, label, editData, disabled }) => (
          <PlatformAutocomplete
            onChange={onChange}
            value={(value as string | null) ?? null}
            label={label}
            initialPlatform={
              (editData as UserEditQueryType | undefined)?.user?.platform ??
              null
            }
            disabled={disabled as boolean | undefined}
          />
        ),
      },
      currentOrganizationId: {
        label: "Текущая организация",
        type: "fieldComponent",
        xs: 12,
        fullWidth: true,
        fieldComponent: ({ onChange, value, label, editData, disabled }) => {
          const typed = editData as UserEditQueryType | undefined;
          return (
            <UserOrganizationAutocomplete
              userId={typed?.user?.id}
              onChange={onChange}
              value={(value as string | null) ?? null}
              label={label}
              initialOrganization={typed?.user?.currentOrganization ?? null}
              anyOrganization={Boolean(
                typed?.user?.isAdmin || typed?.user?.isSuperAdmin
              )}
              disabled={disabled as boolean | undefined}
            />
          );
        },
      },
    },
    getEditData: (data: unknown): UpdateUserInput => {
      const user = (data as UserEditQueryType)?.user;
      return {
        isPartner: user?.isPartner || false,
        platformId: user?.platformId,
        currentOrganizationId: user?.currentOrganizationId,
      };
    },
  },
  admin: {
    label: "Админ",
    type: "form" as const,
    getTitle: getUserTitle,
    updateMutation: UpdateUserMutation,
    editQuery: UserEditQuery,
    schema: {
      isAdmin: {
        label: "Администратор",
        type: "checkbox",
        helperText:
          "Если пользователь имеет доступ в нашу админку. Пункт adminRoutes (пофайловые права доступа, как в референсе) появится, когда поле появится в GraphQL-схеме.",
        xs: 12,
        fullWidth: true,
      },
    },
    getEditData: (data: unknown): UpdateUserInput => {
      const user = (data as UserEditQueryType)?.user;
      return {
        isAdmin: user?.isAdmin || false,
      };
    },
  },
} as const;

export function createEditSchema(
  showComponent: React.ComponentType<{ editData?: unknown; id?: string }>,
  // Вкладка «Админ» видна только суперадмину (референс: Users.js:441-469 —
  // ключ admin дописывается в схему условно по isSuperAdmin из useViewer()).
  isSuperAdmin?: boolean
): EditSchemaDefinition {
  const { admin, ...base } = editSchemaBase;
  return {
    ...base,
    ...(isSuperAdmin ? { admin } : {}),
    details: {
      ...editSchemaBase.details,
      showComponent: showComponent,
    },
  };
}
